import { useState } from 'react';
import { FileText, Pencil, Plus, Trash2 } from 'lucide-react';
import { Carte, Chargement, MessageErreur, Pastille } from '../ui/Primitives';
import { useToast } from '../ui/useToast';
import { ModaleConfirmation } from '../contrats/ModaleConfirmation';
import { VisionneusePiece } from './VisionneusePiece';
import { ModaleResultatEva } from './ModaleResultatEva';
import {
  urlCompteRenduEva,
  useCorrigerEva,
  usePositionnementDossier,
  useRetirerEva,
  useSaisirEva,
  type ResultatEva,
  type TestDuDossier,
} from '../../lib/useEva';
import { ErreurApi } from '../../lib/api';

const CLASSE_ACTION =
  'inline-flex min-h-[36px] items-center gap-1.5 rounded-lg border border-gray-200 px-3 py-1.5 text-[11px] font-semibold text-codialis-navy transition hover:bg-gray-50 [@media(pointer:coarse)]:min-h-[40px]';

function jour(iso: string): string {
  const [annee, mois, quantieme] = iso.split('-');
  return `${quantieme}/${mois}/${annee}`;
}

function messageErreur(erreur: unknown): string | null {
  return erreur instanceof ErreurApi ? erreur.message : null;
}

/**
 * Positionnement pédagogique du dossier (TF-441) : le résultat du test EVA, à
 * côté des tests de positionnement. Lue et tenue par le pôle pédagogique,
 * assistant(e) de site compris(e), et la direction ; les autres pôles ne la
 * montent pas (`peutLireEva`), et l'API la leur refuse.
 */
export function CartePositionnementPedagogique({ apprenantId }: { apprenantId: string }): JSX.Element {
  const notifier = useToast();
  const { data, isLoading, error } = usePositionnementDossier(apprenantId, true);
  const saisir = useSaisirEva(apprenantId);
  const corriger = useCorrigerEva();
  const retirer = useRetirerEva();

  const [edition, setEdition] = useState<ResultatEva | 'nouveau' | null>(null);
  const [aRetirer, setARetirer] = useState<ResultatEva | null>(null);
  const [apercu, setApercu] = useState<ResultatEva | null>(null);

  const fermerEdition = (): void => {
    saisir.reset();
    corriger.reset();
    setEdition(null);
  };

  return (
    <Carte
      titre="Positionnement pédagogique"
      actions={
        <button type="button" onClick={() => setEdition('nouveau')} disabled={!data} className={CLASSE_ACTION}>
          <Plus className="h-3.5 w-3.5" aria-hidden />
          Saisir un résultat EVA
        </button>
      }
      sansBordure
    >
      {isLoading && <Chargement libelle="Lecture du positionnement…" />}
      {error && (
        <div className="p-4 sm:p-6">
          <MessageErreur message={messageErreur(error) ?? 'Le positionnement n’a pas pu être chargé.'} />
        </div>
      )}
      {data && (
        <div className="grid grid-cols-1 gap-4 p-4 sm:p-6 lg:grid-cols-2">
          <section className="min-w-0 rounded-xl bg-gray-50 p-4" aria-label="Test EVA">
            <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-codialis-gray">
              Test EVA · repérage de l’illettrisme
            </div>
            {data.eva ? (
              <SyntheseEva resultat={data.eva} />
            ) : (
              <p className="text-sm text-codialis-gray">
                Aucun test EVA saisi. Il se passe hors de la plateforme : saisissez son score et joignez son compte
                rendu.
              </p>
            )}
            {data.historique.length > 0 && (
              <ul className="mt-3 divide-y divide-gray-200 border-t border-gray-200">
                {data.historique.map((resultat) => (
                  <PassageEva
                    key={resultat.id}
                    resultat={resultat}
                    onApercu={() => setApercu(resultat)}
                    onCorriger={() => setEdition(resultat)}
                    onRetirer={() => setARetirer(resultat)}
                  />
                ))}
              </ul>
            )}
          </section>

          <section className="min-w-0 rounded-xl bg-gray-50 p-4" aria-label="Tests de positionnement">
            <div className="mb-2 text-[10px] font-bold uppercase tracking-widest text-codialis-gray">
              Tests de positionnement
            </div>
            {data.tests.length === 0 ? (
              <p className="text-sm text-codialis-gray">Aucun test de positionnement attribué.</p>
            ) : (
              <ul className="divide-y divide-gray-200">
                {data.tests.map((test) => (
                  <LigneTestDossier key={test.id} test={test} />
                ))}
              </ul>
            )}
          </section>
        </div>
      )}

      {edition && data && (
        <ModaleResultatEva
          resultat={edition === 'nouveau' ? null : edition}
          formats={data.formatsCompteRendu}
          enCours={saisir.isPending || corriger.isPending}
          erreur={messageErreur(edition === 'nouveau' ? saisir.error : corriger.error)}
          onFermer={fermerEdition}
          onEnregistrer={(saisie) => {
            const apresSucces = {
              onSuccess: () => {
                notifier(edition === 'nouveau' ? 'Résultat EVA enregistré.' : 'Résultat EVA corrigé.');
                fermerEdition();
              },
            };
            if (edition === 'nouveau') saisir.mutate(saisie, apresSucces);
            else corriger.mutate({ resultatId: edition.id, saisie }, apresSucces);
          }}
        />
      )}

      <ModaleConfirmation
        ouverte={aRetirer !== null}
        titre="Retirer ce résultat EVA"
        message={
          aRetirer
            ? `Le passage du ${jour(aRetirer.passeLe)} (${aRetirer.score}/${aRetirer.scoreSur}) et son compte rendu seront retirés du dossier. Le journal en garde la trace.`
            : ''
        }
        libelleConfirmer="Retirer"
        enCours={retirer.isPending}
        onAnnuler={() => setARetirer(null)}
        onConfirmer={() => {
          if (!aRetirer) return;
          retirer.mutate(aRetirer.id, {
            onSuccess: () => {
              notifier('Résultat EVA retiré.');
              setARetirer(null);
            },
            onError: (erreur) => {
              notifier(messageErreur(erreur) ?? 'Le résultat n’a pas pu être retiré.');
              setARetirer(null);
            },
          });
        }}
      />

      {apercu?.compteRendu && (
        <VisionneusePiece
          ouverte
          libellePiece={`Compte rendu EVA du ${jour(apercu.passeLe)}`}
          nomFichier={apercu.compteRendu.nomFichier}
          urlBase={urlCompteRenduEva(apercu.id)}
          onFermer={() => setApercu(null)}
        />
      )}
    </Carte>
  );
}

function SyntheseEva({ resultat }: { resultat: ResultatEva }): JSX.Element {
  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className="font-display text-3xl font-extrabold text-codialis-navy">
          {resultat.score}
          <span className="text-base font-bold text-codialis-gray">/{resultat.scoreSur}</span>
        </span>
        <Pastille texte={resultat.accompagnement.reperage} ton={resultat.accompagnement.ton} />
      </div>
      <p className="text-sm text-codialis-navy">
        <span className="font-semibold">Accompagnement conseillé :</span> {resultat.accompagnement.accompagnement}
      </p>
      <p className="text-[11px] text-codialis-gray">Passé le {jour(resultat.passeLe)} · le passage le plus récent fait foi</p>
    </div>
  );
}

function PassageEva({
  resultat,
  onApercu,
  onCorriger,
  onRetirer,
}: {
  resultat: ResultatEva;
  onApercu: () => void;
  onCorriger: () => void;
  onRetirer: () => void;
}): JSX.Element {
  return (
    <li className="py-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-mono text-xs font-semibold text-codialis-navy">
          {resultat.score}/{resultat.scoreSur}
        </span>
        <span className="text-xs text-codialis-gray">{jour(resultat.passeLe)}</span>
        {resultat.faitFoi && <Pastille texte="Fait foi" ton="info" />}
      </div>
      {resultat.commentaire && (
        <p className="mt-1 break-words text-xs text-codialis-navy">{resultat.commentaire}</p>
      )}
      <p className="mt-1 text-[10px] text-codialis-gray">
        Saisi le {new Date(resultat.saisiLe).toLocaleDateString('fr-FR', { timeZone: 'Indian/Reunion' })}
        {resultat.saisiPar ? ` par ${resultat.saisiPar}` : ''}
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        {resultat.compteRendu && (
          <button type="button" onClick={onApercu} className={CLASSE_ACTION}>
            <FileText className="h-3.5 w-3.5" aria-hidden />
            Compte rendu
          </button>
        )}
        <button type="button" onClick={onCorriger} className={CLASSE_ACTION}>
          <Pencil className="h-3.5 w-3.5" aria-hidden />
          Corriger
        </button>
        <button type="button" onClick={onRetirer} className={CLASSE_ACTION}>
          <Trash2 className="h-3.5 w-3.5" aria-hidden />
          Retirer
        </button>
      </div>
    </li>
  );
}

function LigneTestDossier({ test }: { test: TestDuDossier }): JSX.Element {
  return (
    <li className="flex flex-wrap items-center gap-2 py-3">
      <div className="min-w-0 flex-1">
        <div className="break-words text-sm font-semibold text-codialis-navy">{test.titre}</div>
        {test.niveau && <div className="text-[11px] text-codialis-gray">{test.niveau}</div>}
      </div>
      <span className="font-mono text-xs font-semibold text-codialis-navy">
        {test.score === null ? '—' : `${test.score}/${test.scoreSur}`}
      </span>
      <Pastille texte={test.etatLibelle} ton={test.etatTon.replace('badge-', '')} />
    </li>
  );
}
